/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import monitoringService from 'infrastructure/serviceHandlers/Monitoring/Monitoring';
import {
  GetFailedCount,
  GetPurchasesList,
  GetTransactionsByPurchase
} from 'services/api/paymentsApi';
import { TSubscriptionType, TTransaction, TTransactionList } from 'typings';

export type TMyOrdersState = {
  purchases: TSubscriptionType[];
  failedCount: number;
  transactionsByPurchase: Record<string, TTransaction[]>;
  isLoadingPurchases: boolean;
  loadingTransactions: Record<string, boolean>;
};

// List Purchases (Confluence: Invoice and Payment method page). The single call
// made when the billing screen opens. Response is a bare `[purchase]` array.
export const fetchPurchasesList = createAsyncThunk(
  'myOrders/fetchPurchasesList',
  async (spaceId: string, thunkAPI) => {
    try {
      const { data } = await GetPurchasesList(spaceId);
      return (Array.isArray(data) ? data : data.items) as TSubscriptionType[];
    } catch (error) {
      monitoringService.captureException(error, {
        tags: { feature: 'fetchPurchasesList', error_type: 'api_error' }
      });
      return thunkAPI.rejectWithValue('failed');
    }
  }
);

// Failed Payments (Confluence: Invoice and Payment method page). Authoritative
// count behind the failed-payment banner; fetched on screen open and re-fetched
// after a retry / re-subscribe so the banner clears once failures are resolved.
export const fetchFailedCount = createAsyncThunk(
  'myOrders/fetchFailedCount',
  async (_: void, thunkAPI) => {
    try {
      const { data } = await GetFailedCount();
      return data?.failed_count ?? 0;
    } catch (error) {
      monitoringService.captureException(error, {
        tags: { feature: 'fetchFailedCount', error_type: 'api_error' }
      });
      return thunkAPI.rejectWithValue('failed');
    }
  }
);

// List Invoices for a single subscription — fired lazily when an order's
// invoices accordion is expanded. Spec keys this by `purchase_id`.
export const fetchPurchaseTransactions = createAsyncThunk(
  'myOrders/fetchPurchaseTransactions',
  async (
    {
      purchaseId,
      limit,
      skip
    }: {
      purchaseId: string;
      limit?: number;
      skip?: number;
    },
    thunkAPI
  ) => {
    try {
      const response = await GetTransactionsByPurchase(purchaseId, limit, skip);
      return {
        purchaseId,
        transactions: (response.data as TTransactionList)
          .items as TTransaction[]
      };
    } catch (error) {
      monitoringService.captureException(error, {
        tags: { feature: 'fetchPurchaseTransactions', error_type: 'api_error' }
      });
      return thunkAPI.rejectWithValue({ purchaseId });
    }
  }
);

const initialState: TMyOrdersState = {
  purchases: [],
  failedCount: 0,
  transactionsByPurchase: {},
  isLoadingPurchases: false,
  loadingTransactions: {}
};

export const myOrdersSlice = createSlice({
  name: 'myOrders',
  initialState,
  reducers: {
    clearMyOrders: (state) => {
      state.purchases = [];
      state.failedCount = 0;
      state.transactionsByPurchase = {};
      state.loadingTransactions = {};
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchasesList.fulfilled, (state, action) => {
        state.isLoadingPurchases = false;
        state.purchases = action.payload;
      })
      .addCase(fetchPurchasesList.pending, (state) => {
        state.isLoadingPurchases = true;
      })
      .addCase(fetchPurchasesList.rejected, (state) => {
        state.isLoadingPurchases = false;
      })
      // Failed-payment banner count comes from the dedicated /failed-count call,
      // not derived from the purchases list. On error keep the previous count.
      .addCase(fetchFailedCount.fulfilled, (state, action) => {
        state.failedCount = action.payload;
      })
      .addCase(fetchPurchaseTransactions.fulfilled, (state, action) => {
        const { purchaseId, transactions } = action.payload;
        state.transactionsByPurchase[purchaseId] = transactions;
        state.loadingTransactions[purchaseId] = false;
      })
      .addCase(fetchPurchaseTransactions.pending, (state, action) => {
        const { purchaseId } = action.meta.arg;
        state.loadingTransactions[purchaseId] = true;
      })
      .addCase(fetchPurchaseTransactions.rejected, (state, action) => {
        const payload = action.payload as { purchaseId: string } | undefined;
        if (payload?.purchaseId) {
          state.loadingTransactions[payload.purchaseId] = false;
        }
      });
  }
});

export const { clearMyOrders } = myOrdersSlice.actions;
export default myOrdersSlice.reducer;
